import {
  NEEDS_REPLY_TICKET_STATUSES,
  TICKET_STATUS_FILTERS,
  ticketNeedsStaffReply,
  type TicketNeedsReplySource,
  type TicketStatusFilterId,
} from "@/modules/tickets/ticketInboxConfig";

export const ACTIVE_TICKET_STATUSES = ["new", "open", "waiting"] as const;

export type TicketWorkflowStatus =
  | (typeof ACTIVE_TICKET_STATUSES)[number]
  | "resolved";

const BUILTIN_FILTER_IDS = new Set<string>(
  TICKET_STATUS_FILTERS.map((item) => item.id),
);

/** Custom status slug: letter start, then letters/digits/underscore/hyphen. */
export const CUSTOM_STATUS_SLUG = /^[a-z][a-z0-9_-]*$/i;

const RESERVED_STATUS_SLUGS = new Set([
  "all",
  "needs_reply",
  "new",
  "open",
  "waiting",
  "resolved",
]);

export const isValidCustomStatusSlug = (value: string) => {
  const slug = value.trim().toLowerCase();
  if (!slug || RESERVED_STATUS_SLUGS.has(slug)) return false;
  return CUSTOM_STATUS_SLUG.test(slug);
};

export const requiresTicketStatusNote = (
  fromStatus: string,
  toStatus: string,
  options?: { required?: boolean },
) => {
  if (options?.required === false) return false;
  if (fromStatus === toStatus) return false;
  if (toStatus === "waiting" || toStatus === "resolved") return true;
  if (fromStatus === "resolved") return true;
  return false;
};

export const getTicketStatusNotePrompt = (
  fromStatus: string,
  toStatus: string,
) => {
  if (toStatus === "waiting") {
    return "Why is this ticket waiting? Add an internal note for the team.";
  }
  if (toStatus === "resolved") {
    return "Why is this ticket resolved? Add an internal note for the team.";
  }
  if (fromStatus === "resolved") {
    return "Why are you reopening this ticket? Add an internal note for the team.";
  }
  return "Add an internal note explaining this status change.";
};

/**
 * Server-side list filter. `needs_reply` is still status ∈ (new,open);
 * callers refine with `ticketNeedsStaffReply` using `last_inbound_at` /
 * `last_outbound_at` (or loaded message direction). Custom slugs use exact
 * `status@eq`.
 */
export const buildTicketInboxStatusFilter = (
  status: TicketStatusFilterId | TicketWorkflowStatus,
) => {
  if (status === "all") {
    return {};
  }
  if (status === "needs_reply") {
    return { "status@in": `(${NEEDS_REPLY_TICKET_STATUSES.join(",")})` };
  }
  return { "status@eq": status };
};

export const isTicketStatusFilterId = (
  value: string | null | undefined,
): value is TicketStatusFilterId => {
  if (!value?.trim()) return false;
  if (BUILTIN_FILTER_IDS.has(value)) return true;
  return CUSTOM_STATUS_SLUG.test(value);
};

export const parseTicketInboxStatusFilterFromValues = (
  filterValues: Record<string, unknown>,
): TicketStatusFilterId => {
  const inFilter = filterValues["status@in"];
  if (
    typeof inFilter === "string" &&
    inFilter === `(${NEEDS_REPLY_TICKET_STATUSES.join(",")})`
  ) {
    return "needs_reply";
  }
  const eq = filterValues["status@eq"];
  if (
    typeof eq === "string" &&
    isTicketStatusFilterId(eq) &&
    eq !== "all" &&
    eq !== "needs_reply"
  ) {
    return eq;
  }
  return "all";
};

/** URL wins; otherwise infer from the open ticket's status. */
export const resolveTicketInboxStatusFilter = (
  urlStatus: string | null,
  ticketStatus?: string | null,
): TicketStatusFilterId => {
  if (urlStatus && isTicketStatusFilterId(urlStatus)) return urlStatus;
  if (
    ticketStatus &&
    isTicketStatusFilterId(ticketStatus) &&
    ticketStatus !== "needs_reply"
  ) {
    return ticketStatus;
  }
  return "all";
};

export const ticketShowPath = (
  ticketId: string | number,
  status?: string | null,
) => {
  const base = `/tickets/${ticketId}/show`;
  if (status && isTicketStatusFilterId(status) && status !== "all") {
    return `${base}?status=${encodeURIComponent(status)}`;
  }
  return base;
};

export const countTicketsByInboxFilter = (
  tickets: Array<TicketNeedsReplySource & { merged_into_ticket_id?: unknown }>,
  extraStatusIds: string[] = [],
): Record<string, number> => {
  const base: Record<string, number> = {
    all: 0,
    needs_reply: 0,
    new: 0,
    open: 0,
    waiting: 0,
    resolved: 0,
  };
  for (const id of extraStatusIds) {
    const trimmed = id.trim();
    if (trimmed && !(trimmed in base)) base[trimmed] = 0;
  }
  for (const ticket of tickets) {
    if (ticket.merged_into_ticket_id) continue;
    const status = ticket.status?.trim() ?? "";
    base.all += 1;
    if (ticketNeedsStaffReply(ticket)) base.needs_reply += 1;
    if (!status || status === "all" || status === "needs_reply") continue;
    base[status] = (base[status] ?? 0) + 1;
  }
  return base;
};
